import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  CUSTOM_SIZE_OPTION,
  customSizeOptions,
  parseCustomDims,
  getImageSizeInches,
  getPrintAreaInches,
  nearestTierForPricing as computeNearestPrintTier,
  computePreviewCanvasSize,
  getCustomSizeMetaString
} from './customPrintSize';

function PrintOnly({ image, originalImage, onEditImage }) {
  const userImage = image || localStorage.getItem('opticon_last_image');
  const uploadedUserImage = originalImage || userImage;
  const [printType, setPrintType] = useState('canvas');
  const [loading, setLoading] = useState(false);
  const [, setProgress] = useState(0);
  const canvasRef = useRef(null);
  const DPI = 300;
  const [imageSizeCm, setImageSizeCm] = useState(null);
  const [customSize, setCustomSize] = useState(CUSTOM_SIZE_OPTION);
  const [customWidthCm, setCustomWidthCm] = useState('');
  const [customHeightCm, setCustomHeightCm] = useState('');
  const [appliedCustomCropKey, setAppliedCustomCropKey] = useState('');

  const printOptions = [
    { id: 'lustre', label: 'Lustre 300 GSM' },
    { id: 'light-art', label: 'Light Art Paper (210 GSM)' },
    { id: 'canvas', label: 'Canvas (Satin)' },
    { id: 'entrada', label: 'Entrada Cotton Natural Rag 290 GSM' }
  ];

  const { widthIn, heightIn, normalizedSize } = getImageSizeInches(imageSizeCm);
  const cmToInches = (cm) => {
    const parsed = parseFloat(cm);
    if (!parsed || parsed <= 0) return 0;

    return parsed / 2.54;
  };
  const customDimsParsed = () => {
    const widthInches = cmToInches(customWidthCm);
    const heightInches = cmToInches(customHeightCm);

    return parseCustomDims(widthInches, heightInches);
  };

  const isCustomWhMode = customSize === CUSTOM_SIZE_OPTION;
  const customDims = isCustomWhMode ? customDimsParsed() : null;
  const customWidthValue = Number(customWidthCm);
  const customHeightValue = Number(customHeightCm);
  const hasValidCustomSize =
    Number.isFinite(customWidthValue) &&
    Number.isFinite(customHeightValue) &&
    customWidthValue > 0 &&
    customHeightValue > 0;
  const customCropKey = hasValidCustomSize
    ? `${customWidthValue}x${customHeightValue}`
    : '';
  const showApplyCustomCrop =
    isCustomWhMode && hasValidCustomSize && customCropKey !== appliedCustomCropKey;

  const handleApplyCustomCrop = () => {
    if (!showApplyCustomCrop || !onEditImage) return;

    setAppliedCustomCropKey(customCropKey);
    onEditImage({
      aspect: customWidthValue / customHeightValue,
      customWidthCm: customWidthValue,
      customHeightCm: customHeightValue
    });
  };

  const area = getPrintAreaInches(customSize, customDims, widthIn, heightIn);

  const { previewCanvasW, previewCanvasH } = computePreviewCanvasSize(area);

  const nearestTier = computeNearestPrintTier({
    printType,
    customSize,
    customDims,
    widthIn,
    heightIn
  });

  const getPrintPrice = () => nearestTier.price;

  const totalPriceValue = Number(getPrintPrice()) || 0;

  const GST_RATE = 0.1; // 10%
  const gstAmount = totalPriceValue * GST_RATE;
  const totalWithGST = totalPriceValue + gstAmount;
  /* ===== GST + Total ===== */

  const getFilterStyle = () => {
    switch (printType) {
      case 'lustre':
        return { filter: 'contrast(1.08) saturate(1.1)' };

      case 'light-art':
        return { filter: 'contrast(1.02) saturate(0.95)' };

      case 'canvas':
        return { filter: 'contrast(1.05) saturate(1.08)' };

      case 'entrada':
        return { filter: 'contrast(0.98) saturate(0.9) brightness(1.02)' };

      default:
        return {};
    }
  };

  /* ===== IMAGE TO BASE64 ===== */
  const convertImageToBase64 = async (url) => {
    if (!url) return null;

    const res = await fetch(url);
    const blob = await res.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  /* ================= ADD TO CART ================= */
  const handleAddToCart = async () => {
    if (!userImage) {
      alert("Please upload an image first.");
      return;
    }

    if (isCustomWhMode && !hasValidCustomSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Print Size Required',
        text: 'Please enter the print size width and height in cm.'
      });
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      // ✅ ALWAYS upload cropped user image (NOT canvas)
      const resImg = await fetch(userImage, { cache: 'no-store' });
      const blob = await resImg.blob();

      const uploadedImageBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });


      const sizeMeta = getCustomSizeMetaString(
        normalizedSize,
        customSize,
        customDimsParsed()
      );

      const payload = {
        id: 8430,
        quantity: 1,
        meta_data: [
          {
            key: 'image_size',
            value: `${imageSizeCm.widthCm} × ${imageSizeCm.heightCm} cm`
          },

          {
            key: 'custom_size',
            value: sizeMeta
          },

          {
            key: 'print_size',
            value: nearestTier.key
          },

          {
            key: 'print_type',
            value: printType
          },

          {
            key: 'print_price',
            value: totalWithGST.toFixed(2)
          },

          {
            key: 'print_base_price',
            value: totalPriceValue
          },

          {
            key: 'edited_image',
            value: uploadedImageBase64
          },
          {
            key: 'user-uploaded-image',
            value: uploadedUserImage
          }
        ]
      };
      console.log('Payload for API:', payload);
      const response = await axios.post(
        `https://opticonframing.com.au/wp-json/opticon/v1/print-only/add-to-cart`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const percent = Math.round((e.loaded * 100) / e.total);
            setProgress(percent);   // ✅ real dynamic %
          },
        }
      );

      if (response.data.success) {
        setProgress(100);

        setTimeout(() => {
          window.location.href = 'https://opticonframing.com.au/cart';
        }, 700);
      } else {
        throw new Error(response.data.error || 'Server error');
      }

    } catch (err) {
      console.error('❌ Error:', err);
      alert('Failed to add to cart. Check console for details.');
      setLoading(false);
      setProgress(0);
    }
  };

  useEffect(() => {
    if (!userImage || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const filterStyle = getFilterStyle();
      ctx.filter = filterStyle.filter || 'none';

      /* Same as CanvasPrintandFrame / mat+glass: full image scaled into preview rect */
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.filter = 'none';
    };
    img.src = userImage;
  }, [userImage, printType, previewCanvasW, previewCanvasH]);

  useEffect(() => {
    if (!userImage) {
      window.location.href = '/';   // force upload flow
    }
  }, [userImage]);
  console.log('Stored image:', localStorage.getItem('opticon_last_image'));

  useEffect(() => {
    if (!userImage) return;

    const img = new Image();
    img.onload = () => {
      const widthCm = ((img.width / DPI) * 2.54).toFixed(1);
      const heightCm = ((img.height / DPI) * 2.54).toFixed(1);

      setImageSizeCm({
        widthCm,
        heightCm,
        widthPx: img.width,
        heightPx: img.height
      });
    };

    img.src = userImage;
  }, [userImage]);


  return (
    <div className="product-page">

      {/* ===== UPDATED LOADER WITH REQUESTED COLORS ===== */}
      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          {/* Circular Loader */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF', // Requested Color
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            Loading...
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* LEFT PREVIEW */}
      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          width={previewCanvasW}
          height={previewCanvasH}
          className="real-canvas-preview"
        />
      </div>


      {/* RIGHT : PRODUCT INFO */}
      <div className="product-info-panel">

        <h1 className="product-title">Print Only</h1>

        <div className="print-description">
          {printType === 'lustre' && 'Premium lustre finish with vibrant color reproduction.'}
          {printType === 'light-art' && 'Elegant light art paper with soft matte finish.'}
          {printType === 'canvas' && 'Satin canvas print with rich texture and depth.'}
          {printType === 'entrada' && 'Museum-grade Entrada Cotton Natural Rag archival print.'}
        </div>

        {/* Image Size Info */}
        {imageSizeCm && (
          <div className="image-size-row">
            <span className="form-label">Image Size</span>
            <span className="image-size-value">
              {imageSizeCm.widthCm}×{imageSizeCm.heightCm} cm
            </span>

            <button
              type="button"
              className="edit-image-btn"
              onClick={onEditImage}
              title="Edit image"
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          </div>
        )}

        {/* Custom Size */}
        <div className="print-option-row">
          <label className="form-label">Custom Size</label>

          <select
            className="form-select"
            value={customSize}
            onChange={(e) => setCustomSize(e.target.value)}
          >
            <option value={CUSTOM_SIZE_OPTION}>
              Custom width × height (cm)
            </option>

            <option value="">
              Auto (Based on image size)
            </option>

            {customSizeOptions.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {isCustomWhMode && (
          <div className="print-size-canvas-block">
            <label className="form-label print-size-canvas-block__title">
              Print Size
            </label>

            <div className="print-size-canvas-block__row">

              <div className="print-size-canvas-block__field">
                <span
                  className="print-size-canvas-block__dim-label"
                  id="print-canvas-w-label"
                >
                  W
                </span>

                <input
                  id="print-canvas-w-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-w-label"
                  value={customWidthCm}
                  onChange={(e) => setCustomWidthCm(e.target.value)}
                />
              </div>

              <div className="print-size-canvas-block__field">
                <span
                  className="print-size-canvas-block__dim-label"
                  id="print-canvas-h-label"
                >
                  H
                </span>

                <input
                  id="print-canvas-h-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-h-label"
                  value={customHeightCm}
                  onChange={(e) => setCustomHeightCm(e.target.value)}
                />
              </div>

            </div>

            {showApplyCustomCrop && (
              <button
                type="button"
                className="print-size-canvas-block__apply"
                onClick={handleApplyCustomCrop}
              >
                Apply
              </button>
            )}
          </div>
        )}

        {/* Printing option row */}
        <div className="print-option-row">

          <label className="form-label">Printing Options</label>

          <select
            className="form-select"
            value={printType}
            onChange={(e) => setPrintType(e.target.value)}
          >
            {printOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Price + Add to Cart */}
        <div className="price-cart-row">
          <div className="product-price">
            ${totalPriceValue.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)}= ${totalWithGST.toFixed(2)}
          </div>

          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Add to Cart'}
          </button>
        </div>

      </div>
    </div>
  );
}

export default PrintOnly;
